import React from "react";
import { Card } from "@/components/ui/card";

const BOX = "h-[14px] w-[14px] sm:h-4 sm:w-4 rounded-[3px]";

/**
 * 120 koltuk kare grafiği: soldan biz, sağdan rakip.
 * Koyu ton = kesin sonuç girilmiş, açık ton = canlı çetele tahmini.
 * 61. koltukta kırmızı çoğunluk çizgisi.
 */
export default function SeatMap({ seats }) {
  if (!seats) return null;
  const total = seats.total || 120;
  const majority = seats.majority || 61;

  const cells = [];
  for (let i = 0; i < total; i += 1) cells.push({ i, kind: "bos" });
  let p = 0;
  for (let k = 0; k < seats.biz_confirmed; k += 1) cells[p++].kind = "biz_k";
  for (let k = 0; k < seats.biz_projected; k += 1) if (p < total) cells[p++].kind = "biz_t";
  let q = total - 1;
  for (let k = 0; k < seats.karsi_confirmed; k += 1) if (q >= p) cells[q--].kind = "karsi_k";
  for (let k = 0; k < seats.karsi_projected; k += 1) if (q >= p) cells[q--].kind = "karsi_t";

  const style = {
    biz_k: "#15803D", biz_t: "#86EFAC", karsi_k: "#B91C1C", karsi_t: "#FCA5A5", kura_k: "#D97706", bos: "#E2E8F0",
  };

  const legend = [
    ["biz_k", `Bizim kesin ${seats.biz_confirmed}`],
    ["biz_t", `Bizim tahmini ${seats.biz_projected}`],
    ["karsi_k", `Rakip kesin ${seats.karsi_confirmed}`],
    ["karsi_t", `Rakip tahmini ${seats.karsi_projected}`],
    ["bos", `Belirsiz ${seats.undecided}`],
  ];

  const reached = seats.biz_total >= majority;

  return (
    <Card className="p-4 border border-border" data-testid="seat-map">
      <div className="flex flex-wrap items-baseline gap-2">
        <h3 className="font-display font-semibold text-base md:text-lg">Meclis dağılımı</h3>
        <span className="text-sm text-muted-foreground">
          {total} koltuk · çoğunluk {majority}
        </span>
        <span className={`ml-auto rounded-full px-3 py-1 text-xs font-bold ${
          reached ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}
          data-testid="seat-map-status">
          Bizde {seats.biz_total} · Rakip {seats.karsi_total}
          {reached ? " · ÇOĞUNLUK SAĞLANDI" : ` · ${majority - seats.biz_total} koltuk eksik`}
        </span>
      </div>

      <div className="relative mt-6">
        <div className="grid gap-[3px]" style={{ gridTemplateColumns: "repeat(20, minmax(0, 1fr))" }}
          data-testid="seat-map-grid">
          {cells.map((c) => {
            const isMajority = c.i + 1 === majority;
            return (
              <span key={c.i} className={`${BOX} relative`}
                data-testid={isMajority ? "seat-majority-cell" : undefined}
                style={{
                  background: style[c.kind],
                  boxShadow: isMajority ? "0 0 0 2px #DC2626, 0 0 0 4px #fff" : undefined,
                  zIndex: isMajority ? 2 : undefined,
                }}
                title={isMajority ? `${majority}. koltuk — çoğunluk sınırı` : `${c.i + 1}. koltuk`}>
                {isMajority && (
                  <span className="absolute -top-[17px] left-1/2 -translate-x-1/2 whitespace-nowrap rounded px-1 text-[9px] font-bold text-white"
                    style={{ background: "#DC2626" }}>{majority}</span>
                )}
              </span>
            );
          })}
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#DC2626" }}>
          <span className="inline-block h-3 w-3 rounded-[3px]"
            style={{ boxShadow: "0 0 0 2px #DC2626 inset" }} />
          Kırmızı çerçeveli kare {majority}. koltuk — çoğunluk sınırı. Soldan bizim koltuklar bu
          kareyi geçtiyse çoğunluk bizdedir.
        </p>
      </div>

      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {legend.map(([k, label]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-3 w-3 rounded-[3px]" style={{ background: style[k] }} /> {label}
          </span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        {seats.results_count}/48 komitede kesin sonuç girildi. Girilmeyen komiteler canlı çetele
        farkına göre açık tonda tahmin edilir.
      </p>
    </Card>
  );
}
